import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const MORRUGO_CART_BRIDGE_MESSAGE = 'morrugo:cart-lines-update';

/**
 * Returns true only for the hidden same-origin product page used by the
 * MORRUGO custom-size calculator. The storefront preview itself can also be
 * inside an iframe, so window.parent !== window is not enough here.
 */
const isMorrugoCalculatorFrame = () => new URLSearchParams(window.location.search).get('morrugo_embed') === '1';

/**
 * Promote cart / checkout navigation triggered inside the calculator iframe
 * to the full storefront window. This avoids trapping the shopper inside the
 * embedded product page. Preview parameters are preserved only while testing.
 */
if (isMorrugoCalculatorFrame()) {
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link) return;

      let target;
      try {
        target = new URL(link.href, window.location.origin);
      } catch (_) {
        return;
      }

      const path = target.pathname || '';
      const goesToCart = /(^|\/)cart(?:\/|$)/i.test(path);
      const goesToCheckout = /(^|\/)checkouts?(?:\/|$)/i.test(path);
      if (!goesToCart && !goesToCheckout) return;

      try {
        const outer = new URL(window.parent.location.href);
        for (const key of ['preview_theme_id', '_fd', 'pb']) {
          if (outer.searchParams.has(key)) target.searchParams.set(key, outer.searchParams.get(key));
        }
      } catch (_) {
        // no-op
      }

      event.preventDefault();
      event.stopPropagation();
      window.top.location.assign(target.toString());
    },
    true
  );
}

/**
 * Bridge successful cart mutations from the calculator iframe to the parent
 * storefront. We re-fetch the authoritative cart in the parent before
 * resolving Shopify's standard cart event, which keeps the header badge,
 * drawer content, live region, and product forms synchronized.
 *
 * The bridge uses action "update" intentionally so it does not auto-open the
 * cart drawer when a shopper adds a custom-size rug.
 */
if (!isMorrugoCalculatorFrame()) {
  window.addEventListener('message', (messageEvent) => {
    if (messageEvent.origin !== window.location.origin) return;
    if (messageEvent.data?.type !== MORRUGO_CART_BRIDGE_MESSAGE) return;

    const calculatorFrames = Array.from(document.querySelectorAll('.morrugo-inline-custom__frame'));
    const isCalculatorSource = calculatorFrames.some((frame) => frame.contentWindow === messageEvent.source);
    if (!isCalculatorSource) return;

    const fallbackItemCount = Number(messageEvent.data?.itemCount);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferredEventPromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to refresh cart: ${response.status}`);
        return response.json();
      })
      .then((ajaxCart) => {
        const parsedCount = Number(ajaxCart.item_count);
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            itemCount: Number.isFinite(parsedCount) ? parsedCount : fallbackItemCount,
            source: 'morrugo-custom-size-iframe',
            didError: false,
          },
        });
      })
      .catch((error) => {
        if (Number.isFinite(fallbackItemCount) && fallbackItemCount >= 0) {
          deferredEventPromise.resolve({
            cart: { totalQuantity: fallbackItemCount },
            detail: {
              itemCount: fallbackItemCount,
              source: 'morrugo-custom-size-iframe-fallback',
              didError: false,
            },
          });
          return;
        }
        deferredEventPromise.reject(error);
      });
  });
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);

        if (isMorrugoCalculatorFrame() && detail?.didError !== true) {
          window.parent.postMessage(
            {
              type: MORRUGO_CART_BRIDGE_MESSAGE,
              itemCount,
            },
            window.location.origin
          );
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
